import React from 'react';
import { useDashboardFeatureFlags } from '@components';
import { NavTabs } from '@components/common/NavTabs';
import { Banner, Button, HeadContent, List, Table, useMediaQueryContext, useModalContext, } from '@fourthwall/components';
import { AddSubscriptionModal, UpgradeSubscriptionBanner, useSubscription, useTopSubscriptionPlan, } from '../../../../modules/Subscriptions';
import * as S from './Team.styled';
import { RoleListItem } from './components/RoleListItem/RoleListItem';
import { TeamMemberListItem } from './components/TeamMemberListItem/TeamMemberListItem';
export const TeamView = ({ tabsProps, teamTab, rolesTab }) => {
    const media = useMediaQueryContext();
    const { open } = useModalContext();
    const { canUpgradeSubscription, teamMembers, subscriptionData } = useSubscription();
    const { getTopSubscriptionPlanLimitLabel } = useTopSubscriptionPlan();
    const topSubscriptionPlanLimitLabel = getTopSubscriptionPlanLimitLabel('members');
    const teamMembersLimitLabel = topSubscriptionPlanLimitLabel === 'unlimited'
        ? `${topSubscriptionPlanLimitLabel} amount of`
        : topSubscriptionPlanLimitLabel;
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const handleUpgradeSubscription = () => {
        open(AddSubscriptionModal, {
            title: `Upgrade your plan to add more than ${subscriptionData?.members.limit} team members`,
            source: 'settings_team',
        });
    };
    const renderSubscriptionBanner = () => {
        if (!teamMembers.hasLimit || !subscriptionData || !dashboardFeatureFlags.subscriptions.enabled)
            return null;
        // Usage < Limit - Threshold
        if (subscriptionData.members.usage < subscriptionData.members.limit - teamMembers.threshold) {
            return null;
        }
        // Limit - Threshold < Usage < Limit
        if (subscriptionData.members.usage >= subscriptionData.members.limit - teamMembers.threshold &&
            teamMembers.canCreate) {
            if (!canUpgradeSubscription) {
                return (React.createElement(S.SubscriptionBannerContainer, null,
                    React.createElement(Banner, { appearance: "pro" },
                        "You've invited ",
                        subscriptionData.members.usage,
                        " out of",
                        ' ',
                        subscriptionData.members.limit,
                        " team members allowed on your current plan. To stay within the limit, consider removing inactive members - or",
                        ' ',
                        React.createElement("a", { href: "mailto:support@fourthwall.com" }, "contact our support team"),
                        ".")));
            }
            return (React.createElement(S.SubscriptionBannerContainer, null,
                React.createElement(UpgradeSubscriptionBanner, { source: "settings_team", onClick: handleUpgradeSubscription },
                    "You\u2019ve created ",
                    subscriptionData?.members.usage,
                    " out of your",
                    ' ',
                    subscriptionData?.members.limit,
                    "-member limit. Upgrade to",
                    ' ',
                    React.createElement("strong", null, "Fourthwall Pro"),
                    " to add ",
                    teamMembersLimitLabel,
                    " members to your shop.")));
        }
        // Usage >= Limit
        if (!teamMembers.canCreate) {
            if (!canUpgradeSubscription) {
                return (React.createElement(S.SubscriptionBannerContainer, null,
                    React.createElement(Banner, { appearance: "pro" },
                        "You've invited ",
                        subscriptionData.members.usage,
                        " out of",
                        ' ',
                        subscriptionData.members.limit,
                        " team members allowed on your current plan. To invite someone new, please remove an existing member",
                        ' ',
                        React.createElement("a", { href: "mailto:support@fourthwall.com" }, "contact our support team"),
                        ".")));
            }
            return (React.createElement(S.SubscriptionBannerContainer, null,
                React.createElement(UpgradeSubscriptionBanner, { source: "settings_team", onClick: handleUpgradeSubscription },
                    "You\u2019ve hit your ",
                    subscriptionData?.members.limit,
                    "-member limit. Upgrade to",
                    ' ',
                    React.createElement("strong", null, "Fourthwall Pro"),
                    " to add ",
                    teamMembersLimitLabel,
                    " members to your shop.")));
        }
        return null;
    };
    const renderButton = () => {
        if (tabsProps.activeTab === 'team' && teamTab.buttonProps) {
            return React.createElement(Button, { size: "small", appearance: "primary", ...teamTab.buttonProps });
        }
        return null;
    };
    const renderContent = () => {
        if (tabsProps.activeTab === 'team') {
            if (!media.tablet) {
                return (React.createElement(List, { renderItem: (item) => (React.createElement(TeamMemberListItem, { teamMember: item, dropdownProps: teamTab.dropdownProps })), ...teamTab.listProps }));
            }
            return React.createElement(Table, { minWidth: 0, ...teamTab.tableProps });
        }
        if (tabsProps.activeTab === 'roles') {
            if (!media.tablet) {
                return (React.createElement(List, { renderItem: (role) => React.createElement(RoleListItem, { role: role }), ...rolesTab.listProps }));
            }
            return React.createElement(Table, { minWidth: 0, ...rolesTab.tableProps });
        }
        return null;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(HeadContent, { title: "Team \u2013 Settings" }),
        React.createElement(S.TabsContainer, null,
            React.createElement(NavTabs, { ...tabsProps }),
            renderButton()),
        renderSubscriptionBanner(),
        renderContent()));
};
